import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  inject,
} from '@angular/core';
import { EnvironmentService } from '../../../services/environment.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import { DashboardService } from '../../dashboard.service';
import { AdminService } from '../../../admin/admin.service';
import {
  CompositeFilterDescriptor,
  SortDescriptor,
} from '@progress/kendo-data-query';
import {
  FilterService,
  MultipleSortSettings,
} from '@progress/kendo-angular-grid';
import { getFilterMenuText } from '../../../lib/grid-sort-text';
import { Router } from '@angular/router';
import { DateTime } from 'luxon';

@Component({
  selector: 'app-case-usage',
  templateUrl: './case-usage.component.html',
  styleUrl: './case-usage.component.scss',
  standalone: false,
})
export class CaseUsageComponent implements OnInit, OnChanges {
  private adminService = inject(AdminService);
  private dbs = inject(DashboardService);
  private envSvc = inject(EnvironmentService);
  private route = inject(Router);
  private ss = inject(SessionStorageService);

  baseUrl: string;
  environment: any;
  gridData = { headers: [], data: [], total: 0, dropdowns: [] };
  groupByItems = [
    { value: 'unit-case-usage-details', viewValue: 'Unit' },
    { value: 'rep-case-usage-details', viewValue: 'Rep' },
    { value: 'user-case-usage-details', viewValue: 'User' },
  ];
  loading: boolean;
  selectedGroup = 'unit-case-usage-details';

  public pageSize = 25;
  public pageSizeOptions = [25, 50, 75, 100];
  public take = 10;
  public skip = 0;
  public sort: SortDescriptor[] = [];
  public filter: CompositeFilterDescriptor = { logic: 'and', filters: [] };
  public sortSettings: MultipleSortSettings = {
    mode: 'multiple',
    initialDirection: 'desc',
    allowUnsort: true,
  };
  public getFilterMenuText = getFilterMenuText;

  @Input() solutionId: string;
  @Input() start;
  @Input() end;
  @Input() selectedUnit;

  ngOnInit() {
    this.environment = this.envSvc.get();
    this.baseUrl = this.environment.apiBase;
    this.getConfigData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes.solutionId && changes.solutionId.previousValue !== undefined) ||
      (changes.end &&
        changes.end.previousValue === null &&
        changes.end.previousValue !== undefined) ||
      (changes.selectedUnit && changes.selectedUnit.previousValue !== undefined)
    ) {
      this.loading = true;
      this.getGridData();
    }
  }

  getConfigData() {
    this.loading = true;
    this.take = 10;
    this.skip = 0;
    this.sort = [];
    this.gridData = { headers: [], data: [], total: 0, dropdowns: [] };
    this.filter = { logic: 'and', filters: [] };
    this.adminService
      .getUIConfigHeaders({
        uiconfig: 'rb-dash',
        type: this.selectedGroup,
        implementation: 'rb',
      })
      .subscribe(resp => {
        this.gridData.headers = resp['headers'];
        this.getGridData();
      });
  }

  getGridData() {
    const sortObj = {};
    this.sort.forEach(sortItem => {
      if (sortItem['dir']) {
        sortObj[sortItem['field']] = sortItem['dir'].toUpperCase();
      } else {
        this.sort = this.sort.filter(removeItem => removeItem.dir);
      }
    });
    const queryParams = {
      take: this.take,
      skip: this.skip,
      search: {
        solutionid: { selected: [`${this.solutionId}`] },
        SavedTime: {
          start: `${new Date(this.start).toISOString()}`,
          end: `${new Date(this.end).toISOString()}`,
        },
        unitid: { searchString: this.selectedUnit },
      },
      group: this.groupByItems.find(item => item.value === this.selectedGroup)
        .viewValue,
      sort: sortObj,
      name: 'Usage Case Count',
    };
    this.filter.filters.forEach(filterItem0 => {
      filterItem0['filters'].forEach(filterItem1 => {
        if (queryParams.search[filterItem1.field]) {
          queryParams.search[filterItem1.field].selected.push(
            filterItem1.value
          );
        } else {
          queryParams.search[filterItem1.field] = {
            selected: [filterItem1.value],
          };
        }
      });
    });
    this.dbs.getDynamicReport(queryParams).subscribe(res1 => {
      this.gridData.data = res1['data'];
      this.gridData.total = res1['total'];
      this.gridData.dropdowns = res1['dropdowns'];
      this.loading = false;
    });
  }

  dataStateChange(ev) {
    this.loading = true;
    this.take = ev.take;
    this.skip = ev.skip;
    this.sort = ev.sort;
    this.filter = ev.filter;
    this.getGridData();
  }

  searchFilterValueChange(
    values: any[],
    field,
    filterService: FilterService
  ): void {
    filterService.filter({
      filters: values.map(value => ({
        field: field,
        operator: 'eq',
        value,
      })),
      logic: 'or',
    });
  }

  launchCe(launchCase) {
    let startVal;
    let endVal;
    if (typeof this.start === 'object' && typeof this.end === 'object') {
      startVal = DateTime.fromJSDate(this.start).startOf('day').toUTC().toISO();
      endVal = DateTime.fromJSDate(this.end).endOf('day').toUTC().toISO();
    } else {
      startVal = DateTime.fromISO(this.start).startOf('day').toUTC().toISO();
      endVal = DateTime.fromISO(this.end).endOf('day').toUTC().toISO();
    }
    const saveFilters = {
      filters: [
        {
          field: 'solutionId',
          operator: 'equal',
          value: this.solutionId,
        },
        {
          field: 'savedTime',
          operator: 'start',
          value: startVal,
        },
        {
          field: 'savedTime',
          operator: 'end',
          value: endVal,
        },
      ],
    };

    switch (this.selectedGroup) {
      case 'unit-case-usage-details':
        saveFilters.filters.push({
          field: 'unitId',
          operator: 'equal',
          value: launchCase.unitId,
        });
        break;
      case 'rep-case-usage-details':
        saveFilters.filters.push(
          {
            field: 'repCode',
            operator: 'equal',
            value: launchCase.repCode,
          },
          {
            field: 'unitId',
            operator: 'equal',
            value: this.selectedUnit,
          }
        );
        break;
      case 'user-case-usage-details':
        saveFilters.filters.push(
          {
            field: 'userId',
            operator: 'equal',
            value: [launchCase.userId],
          },
          {
            field: 'unitId',
            operator: 'equal',
            value: this.selectedUnit,
          }
        );
        break;
    }

    this.ss.set('CaseExplorerFilters', saveFilters);

    this.route.navigate([`/case-explorer`]);
  }
}
